import {
    CartType,
    FeaturedFlagsContextProvider,
    Footer,
    HeaderContextProvider,
    inquiryModalReducers,
    ShoppingCartContextProvider,
    ToastProvider,
    WSAppContextProvider,
    getUserProps,
} from '@weddingspot/ws-core-components';
import * as React from 'react';
import dynamic from 'next/dynamic';
import App from 'next/app';
import isbot from 'isbot';
import { Provider as ReduxStoreProvider } from 'react-redux';
import { combineReducers, createStore } from 'redux';
import { useRouter } from 'next/router';
import { ThemeProvider } from '@cvent/carina-theme-provider';
import { ThemeProvider as V1ThemeProvider } from '@cvent/carina/components/ThemeProvider';
import wsCarinaTheme from '../utils/carina-theme';

import 'rc-tooltip/assets/bootstrap.css';
import '../styles/globals.scss';
import LinkNext from './../components/ui/link';

import { AnalyticsEvent, logEvent } from '@weddingspot/ws-analytics';
import * as URLS from '../utils/urls';

const TYPEAHEAD_DATA_TTL = 1000 * 60 * 60 * 2;

// import AppHeader from '../components/header/AppHeader';
const AppHeader = dynamic(() => import('../components/header/AppHeader'));
import { ReviewFormPageHeader } from '@weddingspot/ws-core-components';
import {META_ROBOTS as WS_META_ROBOTS, ALB_META_ROBOTS, HOGAN_CONFIG, DD_ENV, DD_SERVICE, DD_VERSION, generateNonce, IS_DEV_ENV} from 'configs/appconfig';

import { datadogRum } from '@datadog/browser-rum';

import {SessionExtend, handleTimeoutToDisplaySessionExtendWidget, extendSessionAtInterval} from '@weddingspot/ws-core-components';
import { Appointments } from '@weddingspot/ws-api-client';
import Head from 'next/head';
import AppLogger from '../utils/logging';

function initializeDatadogRum() {
    const tracingOriginsRegexp = /^(http(s)?:\/\/)?(sg50|ap50|ts50)?\.wedding-spot\.com(.*)$|^(http(s)?:\/\/)?(www.)?\wedding-spot\.com(.*)|^(http(s)?:\/\/)?ws-(render|next|core)-(ap50-|sg50-|ts50-)?ws(dv|pr)\.(us-east-1|us-west-2)\.lb\.(cvent\.com)(.*)$/gm
    const excludedActivityUrlsRegex = /(.*)(amplitude)(.*)|(.*)(analytics.google)(.*)|(.*)(google-analytics)(.*)|(.*)(clarity.ms)(.*)|(.*)(pinterest)(.*)|(.*)(doubleclick)(.*)|(.*)(salesloft)(.*)/gm
    datadogRum.init({
        applicationId: HOGAN_CONFIG.datadog.applicationId,
        clientToken: HOGAN_CONFIG.datadog.clientToken,
        site: 'datadoghq.com',
        service: DD_SERVICE || 'ws-next',
        env: DD_ENV,
        version: DD_VERSION, // deployed version of the application
        sessionSampleRate: Number(HOGAN_CONFIG.datadog.sessionSampleRate),
        sessionReplaySampleRate: Number(HOGAN_CONFIG.datadog.sessionReplaySampleRate),
        trackUserInteractions: true,
        trackResources: true,
        trackLongTasks: true,
        defaultPrivacyLevel:'mask-user-input',
        allowedTracingUrls: [tracingOriginsRegexp],
        trackSessionAcrossSubdomains: true,
        useSecureSessionCookie: true,
        useCrossSiteSessionCookie: true, // This is deprecated in v5 in favor of usePartitionedCrossSiteSessionCookie,
        // but there is a potential breaking issue with use of that param. Please refer:
        // https://github.com/cvent-internal/development-framework/pull/3397/files#diff-b828bf5b40be25b10e9d4dc4f03da7271d4fc404f8660c8390acd4a7bafdd309R17
        // and https://cvent.slack.com/archives/C01EWNQ7K5E/p1718295869782689?thread_ts=1718122761.529549&cid=C01EWNQ7K5E
        traceSampleRate: 25,
        traceContextInjection: 'sampled',
        excludedActivityUrls: [excludedActivityUrlsRegex],
    })
}

function setDDGlobalContext(){
    const env = DD_ENV || 'local';
    const version = DD_VERSION || 'undefined';
    getUserProps(env,version).then((userProps: any) => {
        Object.entries(userProps).forEach(([key, value]) => {
            datadogRum.setGlobalContextProperty(key, value)
        })
    });
}

const store = createStore(
    combineReducers({
        ...inquiryModalReducers,
    })
);

const getCSPHeader = (nonce: string) => {  
    const localhostCspDirective = IS_DEV_ENV  
        ? "http://localhost:4000 http://local.wedding-spot.com:8999 http://localhost:8999 http://local.wedding-spot.com:4000 ws://localhost:4000/_next/"  
        : ""; 

    const localhostUnsafeEval = IS_DEV_ENV  
        ? " 'unsafe-eval'"
        : ""; 

  
    return (  
        "default-src 'self' https://*.wedding-spot.com https://*.socialtables.com; " +  
        "font-src 'self' https://*.wedding-spot.com https://*.cloudfront.net https://fonts.gstatic.com https://*.cvent.com data: blob:; " +  
        "connect-src " + localhostCspDirective + " https://noembed.com/ https://*.ingest.sentry.io https://data.pendo.io https://google.com " +  
            "https://*.clarity.ms/collect https://vimeo.com/ https://browser-intake-datadoghq.com https://*.cvent.cloud/ https://*.cvent.com " +  
            "https://rum.browser-intake-datadoghq.com https://*.wedding-spot.com https://bat.bing.com https://www.google.co.in https://*.google-analytics.com https://*.analytics.google.com https://*.googletagmanager.com " +  
            "https://api.amplitude.com/ https://j.clarity.ms/collect https://maps.googleapis.com/ https://ad.doubleclick.net https://*.g.doubleclick.net https://*.google.com https://www.googleadservices.com https://*.googlesyndication.com ; " +  
        "frame-src 'self' https://*.cvent.com https://*.g.doubleclick.net https://*.google.com https://www.google.co.in https://td.doubleclick.net/ https://www.youtube.com/ https://player.vimeo.com/ https://*.socialtables.com/ https://www.googletagmanager.com; " +  
        "script-src-elem 'self' 'nonce-" + nonce + "' " + localhostCspDirective +   
            " https://player.vimeo.com/ https://www.youtube.com/ https://*.cloudfront.net https://tagmanager.google.com " +  
            "https://www.gstatic.com https://www.google.com https://www.google.co.in https://www.googleadservices.com https://*.googlesyndication.com " +  
            "https://*.cvent.com https://www.google-analytics.com https://*.googletagmanager.com https://*.wedding-spot.com https://www.datadoghq-browser-agent.com " +  
            "https://*.pendo.io https://cdn.amplitude.com https://www.clarity.com https://www.clarity.ms https://www.youtube.com " +  
            "https://assets.pinterest.com https://widgets.pinterest.com https://bat.bing.com ; " +  
        "script-src 'nonce-" + nonce + "'" + localhostUnsafeEval + " https://bat.bing.com; " +
        "style-src 'unsafe-inline' " + localhostCspDirective + 
            " https://*.wedding-spot.com https://googletagmanager.com https://tagmanager.google.com https://fonts.googleapis.com; " +  
        "img-src 'self' " + localhostCspDirective +   
            "https://images-lower.cvent.com https://bat.bing.com https://*.s3.amazonaws.com/images/ https://i.vimeocdn.com/ https://*.google-analytics.com https://*.googletagmanager.com " +  
            "https://i.ytimg.com https://images.cvent.com/ws/images/venues/ https://c.bing.com/ https://*.cvent.com/ https://pro.fontawesome.com/ " +  
            "https://c.clarity.ms/c.gif https://*.cloudfront.net https://maps.googleapis.com/maps/ https://*.socialtables.com/ https://www.googleadservices.com https://*.googlesyndication.com " +  
            "https://*.gstatic.com https://i.pinimg.com https://log.pinterest.com " +  
            "https://*.cvent.com https://*.wedding-spot.com https://data.pendo.io/ https://www.google.co.in https://ad.doubleclick.net https://*.g.doubleclick.net https://*.google.com data: blob:;" +
        "worker-src 'self' https://*.wedding-spot.com https://bat.bing.com blob:; "
    );  
};  

function MyApp({ Component, pageProps, isBot, profileVersion, metaRobots, nonceValue }: any) {
    const router = useRouter();
    const reviewFormPagePath = '^(\/review/.*/?)$'
    const defaultHeaderContextProps = {
        headerItemClicked: (item: string) => logEvent(AnalyticsEvent.EVTNM_CLICKED_HEADER_ITEM, { Item: item }),
        headerLocationItemClicked: (source: string, value: string) =>
            logEvent(AnalyticsEvent.EVTNM_CLICKED_LOCATIONS_ITEM, { Source: source, Value: value }),
        onSearchResultClicked: (link: string, inputValue: string) =>
            logEvent(AnalyticsEvent.EVTNM_CLICKED_SEARCH, { Link: link }, undefined, inputValue),
        dataUrl: URLS.TYPEAHEAD_CDN_URL,
        dataTTL: TYPEAHEAD_DATA_TTL,
        profileVersion: profileVersion,
        nonceValue: nonceValue,
        options: {
            maxResultsPerSection: 5,
            locationUrlFormatter: URLS.getLoacationFullURL,
            venueUrlFormatter: URLS.getVenueURL,
            styleUrlFormatter: URLS.getStyleSrpUrl,
        },
    };

    const onSuccessfulOutreach = (request: Appointments.MassInquiryRequestParams, sourcePage?: string) => {
        logEvent(AnalyticsEvent.EVTNM_SUCCESSFUL_COUPLE_OUTREACH, {
            source: sourcePage ? sourcePage : 'venues SRP shopping cart',
            VenueCount: request.vendor_ids.length,
            ...request.venues_analytics,
        });
    };

    const [isOpen, setIsOpen] = React.useState(false)
    React.useEffect(() => {
        initializeDatadogRum();
        setDDGlobalContext();
        AppLogger.createLogger();
        extendSessionAtInterval();
        handleTimeoutToDisplaySessionExtendWidget(setIsOpen);

        const scriptTags = document.querySelectorAll('script');
        scriptTags.forEach((script) => {
            const nonce = nonceValue;
            script.setAttribute('nonce', nonce);
        });
    }, []);


    return (
        <ReduxStoreProvider store={store}>
            <V1ThemeProvider theme={wsCarinaTheme}>
            <ThemeProvider theme={wsCarinaTheme}>
                <WSAppContextProvider rootNodeId='root' isNext={true} linkComponent={LinkNext} {...({} as any)}>
                    <ToastProvider>
                        <FeaturedFlagsContextProvider isBot={isBot}>
                            <ShoppingCartContextProvider cartType={CartType.VENUES} itemsMax={10} onSuccessfulOutreach={onSuccessfulOutreach}>
                                <div id='root' role='main'>
                                    <Head>
                                        <title>Wedding Venues &amp; Planning Tools | Wedding Spot</title>
                                        <meta name="robots" content={metaRobots} />
                                        <meta name="theme-color" content='#881155' />
                                        <meta name="application-name" content='Wedding Spot, Search, price &amp; compare wedding venues' />
                                    </Head>
                                    <HeaderContextProvider {...defaultHeaderContextProps}>
                                        <SessionExtend isOpen={isOpen} setIsOpen={setIsOpen} />
                                        {router.pathname.match(reviewFormPagePath) ? <ReviewFormPageHeader /> : <AppHeader />}
                                        <Component {...pageProps} />
                                        {router.pathname.match(reviewFormPagePath) ? null : <Footer />}
                                    </HeaderContextProvider>
                                </div>
                            </ShoppingCartContextProvider>
                        </FeaturedFlagsContextProvider>
                    </ToastProvider>
                </WSAppContextProvider>
            </ThemeProvider>
            </V1ThemeProvider>
        </ReduxStoreProvider>
    );
}

MyApp.getInitialProps = async (appContext) => {
    const appProps = await App.getInitialProps(appContext);

    const nonce = generateNonce();
    appContext.ctx.res.setHeader('Content-Security-Policy', getCSPHeader(nonce));

    let META_ROBOTS='';

    if (!appContext.ctx.req.headers['x-app-domain-hit']) {
        META_ROBOTS = ALB_META_ROBOTS;
    }else{
        META_ROBOTS = WS_META_ROBOTS;
    }

    appContext.ctx.req.nonceValue = nonce;

    return {
        isBot: isbot(appContext.ctx.req.headers['user-agent']),
        profileVersion: appContext.ctx.req.headers['x-profile-version'],
        nonceValue: nonce,
        metaRobots: META_ROBOTS,
        ...appProps,
    };
};

export default MyApp;
